/**
 * TA Check-In cluster index — pure computation, no DOM.
 *
 * One number per cluster answering "who do we help first". There are no
 * absolute good/bad anchors — every criterion becomes a percentage of the
 * cluster average (score = value / avg * 100, so 100 = exactly average),
 * then the index is the weighted average of those scores. Criteria with no
 * data are dropped and the remaining weights re-normalised, so a missing
 * feed (e.g. Lever pipeline risk) never silently drags a cluster down.
 *
 * Because scores are relative they always average to 100 and one cluster is
 * always highest: the index says WHERE to help first, not whether the
 * absolute workload is acceptable.
 *
 * Ported from the ta-cockpit-local prototype (public/app.js).
 */
import type { CapacityState, CheckInConfig, CheckInData, CheckInRole } from '../types/taCheckin';

export interface Criterion {
  id:
    | 'role_load'
    | 'interview_load'
    | 'pipeline_health'
    | 'pipeline_volume'
    | 'urgency'
    | 'complexity';
  label: string;
  unit: string;
  fmt: 'num' | 'pct';
  /**
   * True when a LOW displayed value means MORE pressure — currently only
   * pipeline volume: thin pipelines are the ones that need sourcing. The
   * displayed value stays the human-readable one (median candidates per
   * posting); only the scoring is inverted. See `pressureOf`.
   */
  invert?: boolean;
}

export const CRITERIA: Criterion[] = [
  { id: 'role_load', label: 'Role load', unit: ' / FTE', fmt: 'num' },
  { id: 'interview_load', label: 'Interviews', unit: ' / FTE / wk', fmt: 'num' },
  { id: 'pipeline_health', label: 'Pipeline risk', unit: '%', fmt: 'pct' },
  { id: 'pipeline_volume', label: 'Pipeline volume', unit: ' cand. / role', fmt: 'num', invert: true },
  { id: 'urgency', label: 'Role urgency', unit: '%', fmt: 'pct' },
  { id: 'complexity', label: 'Role complexity', unit: '%', fmt: 'pct' },
];

/**
 * Convert a displayed value into the "pressure" quantity that gets scored.
 *
 * For inverted criteria this is the reciprocal, so that scarcity reads as
 * pressure. It is applied BEFORE the relative comparison, not by flipping the
 * finished score (i.e. avg(1/v) rather than avg(v)/v) — that keeps the
 * property the whole index rests on: scores still average to exactly 100.
 *
 * The 0.5 floor stops an empty pipeline (median 0) becoming infinite; it caps
 * such a cluster's pressure at the level of "half a candidate per role", which
 * is already far above any realistic average.
 */
function pressureOf(crit: Criterion, value: number): number {
  return crit.invert ? 1 / Math.max(value, 0.5) : value;
}

export type Flag = 'crit' | 'watch' | 'ok' | 'none';
export type Rag = 'red' | 'amber' | 'green';

export interface ClusterCapacityNow {
  base: number | null;
  now: number | null;
  recruiterBase: number | null;
  recruiterNow: number | null;
  recruiterOut: number;
  internRequested: number;
  internEffective: number;
  /** True when the intern pool was short and this cluster got scaled back. */
  scaled: boolean;
  lost: number;
}

export interface CriterionRow extends Criterion {
  value: number | null;
  score: number | null;
  weight: number;
  flag: Flag;
}

export interface ClusterIndex {
  cluster: string;
  cap: ClusterCapacityNow;
  rows: CriterionRow[];
  index: number | null;
  driver: CriterionRow | null;
  missing: CriterionRow[];
}

const num = (v: unknown): number | null => {
  const n = parseFloat(String(v ?? '').replace(/[%,]/g, ''));
  return isNaN(n) ? null : n;
};

function sheetMetric(data: CheckInData, cluster: string, key: string): number | null {
  const m = data.metrics[key];
  return m ? num(m[cluster]) : null;
}

/**
 * Fallback when the capacity tab is unreadable: the check-in tab's own load
 * is roles ÷ capacity, so one combined capacity figure is recoverable.
 */
function derivedCapacity(data: CheckInData, cluster: string): number | null {
  const open = sheetMetric(data, cluster, 'Open Roles');
  const load = sheetMetric(data, cluster, 'Cluster Role Load');
  return open != null && load ? open / load : null;
}

export function sheetInternFte(data: CheckInData, cluster: string): number | null {
  return data.capacity.ok ? (data.capacity.byCluster?.[cluster]?.internFte ?? null) : null;
}

/** Recruiter FTE per cluster, straight from the TA Capacity Setup tab. */
export function recruiterBase(data: CheckInData, cluster: string): number | null {
  if (data.capacity.ok) {
    const b = data.capacity.byCluster?.[cluster];
    if (b && b.recruiterFte != null) return b.recruiterFte;
  }
  // No split available — total capacity minus the sheet's intern allocation,
  // so recruiters and interns are still not double-counted.
  const derived = derivedCapacity(data, cluster);
  if (derived == null) return null;
  return Math.max(derived - (sheetInternFte(data, cluster) || 0), 0);
}

export interface InternPool {
  count: number;
  out: number;
  availableFte: number;
  requestedFte: number;
}

export function internPool(data: CheckInData, state: CapacityState, cfg: CheckInConfig): InternPool {
  const count = state.internCount ?? 0;
  const out = Math.min(state.internsOut || 0, count);
  const w = cfg.intern_fte ?? 1;
  return {
    count,
    out,
    availableFte: Math.max(count - out, 0) * w,
    requestedFte: data.clusters.reduce((s, c) => s + (Number(state.alloc[c]) || 0), 0) * w,
  };
}

/**
 * If more intern capacity is allocated than exists — an OOO or an over-eager
 * reallocation — every cluster is scaled back proportionally, so the index
 * always reflects capacity that actually exists.
 */
function allocScale(data: CheckInData, state: CapacityState, cfg: CheckInConfig): number {
  const p = internPool(data, state, cfg);
  if (p.requestedFte <= p.availableFte || p.requestedFte === 0) return 1;
  return p.availableFte / p.requestedFte;
}

export function capacityFor(
  data: CheckInData,
  state: CapacityState,
  cfg: CheckInConfig,
  cluster: string
): ClusterCapacityNow {
  const rBase = recruiterBase(data, cluster);
  const rOut = (state.recruitersOut?.[cluster] || 0) * (cfg.recruiter_fte ?? 1);
  const rNow = rBase == null ? null : Math.max(rBase - rOut, 0);

  const requested = (Number(state.alloc[cluster]) || 0) * (cfg.intern_fte ?? 1);
  const effective = requested * allocScale(data, state, cfg);

  const base = rBase == null ? null : rBase + (sheetInternFte(data, cluster) ?? 0);
  const now = rNow == null ? null : rNow + effective;

  return {
    base,
    now,
    recruiterBase: rBase,
    recruiterNow: rNow,
    recruiterOut: rOut,
    internRequested: requested,
    internEffective: effective,
    scaled: Math.abs(requested - effective) > 0.005,
    lost: base == null || now == null ? 0 : Math.max(base - now, 0),
  };
}

/**
 * Interviews per week for a cluster, live from the warehouse when available.
 *
 * The trailing 7 days is the index input rather than the coming 7 days: it is
 * a complete week, whereas "next 7 days" grows all week as interviews get
 * booked and would understate every cluster early in the week — unevenly, since
 * clusters book at different lead times.
 *
 * Falls back to the sheet's `Cluster Interview Load (per week)`, which a weekly
 * agent writes, whenever the warehouse is unavailable.
 */
export function interviewsPerWeek(data: CheckInData, cluster: string): number | null {
  const live = data.interviewLoad;
  if (live?.ok && live.byCluster?.[cluster]) return live.byCluster[cluster].last7d;
  return sheetMetric(data, cluster, 'Cluster Interview Load (per week)');
}

/** Where the interview figure on screen came from — surfaced in the UI. */
export function interviewLoadSource(data: CheckInData): 'live' | 'sheet' {
  return data.interviewLoad?.ok ? 'live' : 'sheet';
}

/**
 * How stale the warehouse's Lever data was when this payload was built, in
 * hours. Derived from two timestamps inside the payload rather than the wall
 * clock, so it is pure and does not creep upward while the tab sits open.
 */
export function warehouseAgeHours(data: CheckInData): number | null {
  const asOf = data.interviewLoad?.asOf ?? data.pipeline?.asOf;
  if (!asOf) return null;
  const synced = new Date(asOf).getTime();
  const generated = new Date(data.generatedAt).getTime();
  if (isNaN(synced) || isNaN(generated)) return null;
  return Math.max(Math.round((generated - synced) / 3600000), 0);
}

export function openRolesFor(data: CheckInData, cluster: string): number {
  const n = data.roles.filter((r) => (r.sheetCluster || r.department) === cluster).length;
  return n || sheetMetric(data, cluster, 'Open Roles') || 0;
}

export function flagFor(cfg: CheckInConfig, score: number | null): Flag {
  if (score == null) return 'none';
  if (score >= (cfg.row_flag?.critical ?? 115)) return 'crit';
  if (score >= (cfg.row_flag?.watch ?? 100)) return 'watch';
  return 'ok';
}

export function indexRag(cfg: CheckInConfig, idx: number | null): Rag {
  if (idx == null) return 'green';
  if (idx >= (cfg.index_rag?.red ?? 115)) return 'red';
  if (idx >= (cfg.index_rag?.amber ?? 100)) return 'amber';
  return 'green';
}

/**
 * Raw value for one criterion. Role load and interviews are divided by
 * available FTE so clusters of different sizes compare fairly.
 */
function rawValue(
  data: CheckInData,
  cluster: string,
  crit: Criterion,
  cap: ClusterCapacityNow
): number | null {
  switch (crit.id) {
    case 'role_load':
      return cap.now ? openRolesFor(data, cluster) / cap.now : null;
    case 'interview_load': {
      const iv = interviewsPerWeek(data, cluster);
      return iv != null && cap.now ? iv / cap.now : null;
    }
    case 'pipeline_health': {
      if (!data.pipeline.ok) return null;
      const b = data.pipeline.byCluster?.[cluster];
      return b && b.riskPct != null ? b.riskPct : null;
    }
    case 'pipeline_volume': {
      if (!data.pipeline.ok) return null;
      const b = data.pipeline.byCluster?.[cluster];
      return b && b.medianActive != null ? b.medianActive : null;
    }
    case 'urgency':
      return sheetMetric(data, cluster, 'Role Urgency');
    case 'complexity':
      return sheetMetric(data, cluster, 'Role Complexity');
    default:
      return null;
  }
}

/**
 * Everything is computed for all clusters at once, because each score is
 * relative to the average across them. Returned sorted, highest index first.
 */
export function computeIndexes(data: CheckInData, state: CapacityState): ClusterIndex[] {
  const cfg = data.config;
  const clusters = data.clusters;
  const weights = cfg.index_weights || {};
  const caps: Record<string, ClusterCapacityNow> = {};
  clusters.forEach((c) => {
    caps[c] = capacityFor(data, state, cfg, c);
  });

  const raw: Record<string, Record<string, number | null>> = {};
  CRITERIA.forEach((crit) => {
    raw[crit.id] = {};
    clusters.forEach((c) => {
      raw[crit.id][c] = rawValue(data, c, crit, caps[c]);
    });
  });

  // Average the PRESSURE across the clusters that have a value, then
  // score = pressure / avg * 100. For normal criteria pressure is the value
  // itself; for inverted ones it is its reciprocal, so scarcity scores high.
  const score: Record<string, Record<string, number | null>> = {};
  CRITERIA.forEach((crit) => {
    const vals = clusters
      .map((c) => raw[crit.id][c])
      .filter((v): v is number => v != null && isFinite(v))
      .map((v) => pressureOf(crit, v));
    const avg = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
    score[crit.id] = {};
    clusters.forEach((c) => {
      const v = raw[crit.id][c];
      score[crit.id][c] =
        v == null || !isFinite(v) || !avg ? null : (pressureOf(crit, v) / avg) * 100;
    });
  });

  return clusters
    .map((cluster) => {
      const rows: CriterionRow[] = CRITERIA.map((crit) => ({
        ...crit,
        value: raw[crit.id][cluster],
        score: score[crit.id][cluster],
        weight: Number(weights[crit.id]) || 0,
        flag: flagFor(cfg, score[crit.id][cluster]),
      }));
      const used = rows.filter((r) => r.score != null && r.weight > 0);
      const wSum = used.reduce((s, r) => s + r.weight, 0);
      const index = wSum ? used.reduce((s, r) => s + (r.score as number) * r.weight, 0) / wSum : null;
      // Biggest contributor = the row pushing this cluster furthest above average.
      const driver =
        used
          .filter((r) => (r.score as number) > 100)
          .sort(
            (a, b) =>
              ((b.score as number) - 100) * b.weight - ((a.score as number) - 100) * a.weight
          )[0] || null;
      return { cluster, cap: caps[cluster], rows, index, driver, missing: rows.filter((r) => r.score == null) };
    })
    .sort((a, b) => (b.index ?? -1) - (a.index ?? -1));
}

/** Default what-if state: the sheet's own intern split, nobody out. */
export function sheetDefaults(data: CheckInData): CapacityState {
  const alloc: Record<string, number> = {};
  data.clusters.forEach((c) => {
    alloc[c] = sheetInternFte(data, c) ?? 0;
  });
  const recruitersOut: Record<string, number> = {};
  data.clusters.forEach((c) => {
    recruitersOut[c] = 0;
  });
  const internCount =
    data.capacity.ok && data.capacity.internsTotal != null
      ? data.capacity.internsTotal
      : data.clusters.reduce((s, c) => s + (alloc[c] || 0), 0);
  return { internCount, internsOut: 0, alloc, recruitersOut };
}

/**
 * The sheet's role rows as every reader of them expects.
 *
 * ⚠️ SHARED, because the rows are now read on two tabs. A role with no rating is `Unrated` rather
 * than blank (a blank tier is a column nobody can click), and urgency is lower-cased because the
 * priority × urgency matrix buckets on that string — the sheet column is hand-typed, so "High" and
 * "high" arrive from the same person in the same week. The Company View rendered the matrix from
 * the raw rows for its first hour and lost every capitalised one into "no urgency".
 */
export function normaliseCheckInRoles(roles: CheckInRole[]): CheckInRole[] {
  return roles.map((r) => ({
    ...r,
    priority: (r.priority || '').trim() || 'Unrated',
    department: r.department || 'Unassigned',
    urgency: (r.urgency || '').toLowerCase(),
  }));
}
